// Reusable blocks for verificationConfigs.jsx (rendered through a config's extra()).
import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import * as XLSX from 'xlsx';
import { CheckSquare, Square, X, Loader2, ChevronRight, ChevronDown, Trash2 } from 'lucide-react';
import { getRoute, postRoute } from '../../api/AccountsAPI/configVerificationAPI';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

const fmt = (v) => (v != null && v !== '' ? formatIndianCurrency(v) : '');
const th = 'px-3 py-2 text-[10px] font-bold text-white uppercase tracking-wider whitespace-nowrap';
const td = 'px-3 py-2 text-xs text-gray-700 dark:text-gray-300';

// Titled card matching the detail sections.
export const Block = ({ title, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700">
        {title && <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{title}</p>}
        {children}
    </div>
);

// Items the verifier must tick one by one (legacy "Please Verify ItemCodes").
// columns: [{ label, render(item), align? }]; checked state lives in ext.checked (index → bool).
export const CheckedItemsTable = ({ title, items, columns, ext, setExt, rowClass, footer, onItemClick }) => {
    const checked = ext.checked || {};
    const all = items.length > 0 && items.every((_, i) => checked[i]);
    const toggle = (i) => setExt((p) => ({ ...p, checked: { ...(p.checked || {}), [i]: !(p.checked || {})[i] } }));
    const toggleAll = () => setExt((p) => ({ ...p, checked: all ? {} : Object.fromEntries(items.map((_, i) => [i, true])) }));

    return (
        <Block title={title}>
            <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Tick every item to confirm it has been checked — {items.filter((_, i) => checked[i]).length}/{items.length} verified
                </p>
                <button type="button" onClick={toggleAll} className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
                    {all ? 'Clear all' : 'Check all'}
                </button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="w-full">
                    <thead>
                        <tr className="bg-gradient-to-r from-indigo-600 to-purple-600">
                            <th className={th}>Chk</th>
                            {columns.map((c) => <th key={c.label} className={`${th} ${c.align === 'right' ? 'text-right' : 'text-center'}`}>{c.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it, i) => (
                            <tr key={i} className={`border-t border-gray-100 dark:border-gray-700 ${rowClass ? rowClass(it) : ''}`}>
                                <td className={`${td} text-center`}>
                                    <button type="button" onClick={() => toggle(i)} className="text-indigo-600 dark:text-indigo-400">
                                        {checked[i] ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                    </button>
                                </td>
                                {columns.map((c) => (
                                    <td key={c.label} className={`${td} ${c.align === 'right' ? 'text-right' : c.align === 'left' ? 'text-left' : 'text-center'}`}>
                                        {c.link && onItemClick
                                            ? <button type="button" onClick={() => onItemClick(it)} className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">{c.render(it)}</button>
                                            : c.render(it)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {!items.length && (
                            <tr><td colSpan={columns.length + 1} className={`${td} text-center py-4`}>No items found</td></tr>
                        )}
                    </tbody>
                    {footer && <tfoot><tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">{footer}</tr></tfoot>}
                </table>
            </div>
        </Block>
    );
};

// Plain read-only table. rows: arrays of cell values; alignRight: column indexes; foot?: footer cells.
export const SimpleTable = ({ heads, rows, alignRight = [], foot }) => (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <table className="w-full">
            <thead>
                <tr className="bg-gray-100 dark:bg-gray-700/60">
                    {heads.map((h, i) => (
                        <th key={h || i} className={`px-3 py-2 text-[10px] font-bold text-gray-500 dark:text-gray-300 uppercase ${alignRight.includes(i) ? 'text-right' : 'text-center'}`}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, ri) => (
                    <tr key={ri} className="border-t border-gray-100 dark:border-gray-700">
                        {row.map((c, ci) => <td key={ci} className={`${td} ${alignRight.includes(ci) ? 'text-right' : 'text-center'}`}>{c}</td>)}
                    </tr>
                ))}
                {!rows.length && <tr><td colSpan={heads.length} className={`${td} text-center py-4`}>No records</td></tr>}
            </tbody>
            {foot && (
                <tfoot>
                    <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
                        {foot.map((c, i) => <td key={i} className={`${td} font-bold text-emerald-600 ${alignRight.includes(i) ? 'text-right' : 'text-center'}`}>{c}</td>)}
                    </tr>
                </tfoot>
            )}
        </table>
    </div>
);

// All item rows ticked?
export const allItemsChecked = (items, ext) => !!items && items.length > 0 && items.every((_, i) => (ext.checked || {})[i]);

// Item → MRR breakup (legacy TradeItemCodeSummaryPopup; route/params override for other screens'
// popups with the same columns, e.g. Purchase/NewStockIssueSummaryPopup). Portaled above the detail popup.
export const TradeItemSummaryModal = ({ itemCode, invoiceNo, onClose, route = 'Accounts/TradeItemCodeSummaryPopup', params }) => {
    const [rows, setRows] = useState(null);
    const query = params || { Itemcode: itemCode, InvoiceNo: invoiceNo };
    const queryKey = JSON.stringify(query);
    useEffect(() => {
        let live = true;
        getRoute(route, JSON.parse(queryKey))
            .then((r) => { if (live) setRows((r && r.Data) || []); })
            .catch(() => { if (live) setRows([]); });
        return () => { live = false; };
    }, [route, queryKey]);

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <p className="font-bold">Item {itemCode} — Stock Breakup</p>
                    <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 max-h-[60vh] overflow-auto">
                    {rows === null ? (
                        <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
                    ) : (
                        <table className="w-full rounded-xl border border-gray-200 dark:border-gray-700">
                            <thead>
                                <tr className="bg-gray-100 dark:bg-gray-700">
                                    {['Item Code', 'MRR No', 'Units', 'Basic Price', 'Quantity', 'Amount'].map((h) => (
                                        <th key={h} className="px-3 py-2 text-[10px] font-bold text-gray-500 dark:text-gray-300 uppercase">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                                        <td className={`${td} text-center`}>{r.Pitemcode}</td>
                                        <td className={`${td} text-center`}>{r.TMrrs}</td>
                                        <td className={`${td} text-center`}>{r.Punits}</td>
                                        <td className={`${td} text-right`}>{fmt(r.pbasicpric)}</td>
                                        <td className={`${td} text-center`}>{r.pquantity}</td>
                                        <td className={`${td} text-right`}>{fmt(r.pamount)}</td>
                                    </tr>
                                ))}
                                {!rows.length && <tr><td colSpan={6} className={`${td} text-center py-4`}>No records</td></tr>}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export { fmt };

// Work In Progress resubmit: Client → Sub Client → PO cascade for a fixed cost center (legacy EditWorkInProgress).
// Values live in the resubmit form: Clientcode, SubClientcode, PONumber.
const listOf = (r) => (r && Array.isArray(r.Data) ? r.Data : Array.isArray(r) ? r : []);
const selCls = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
const lblCls = 'block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1';

export const WIPPartyEditor = ({ ccCode, wpId, values, setValues }) => {
    const [clients, setClients] = useState([]);
    const [subClients, setSubClients] = useState([]);
    const [pos, setPos] = useState([]);
    const { Clientcode: client, SubClientcode: subClient } = values;

    useEffect(() => {
        let live = true;
        getRoute('Accounts/GetClientsbyCCForWorkProgress', { CCCode: ccCode })
            .then((r) => { if (live) setClients(listOf(r)); }).catch(() => { if (live) setClients([]); });
        return () => { live = false; };
    }, [ccCode]);

    useEffect(() => {
        let live = true;
        setSubClients([]);
        if (client) {
            getRoute('Accounts/GetSubClientsbyClientForWP', { CCCode: ccCode, clientcode: client })
                .then((r) => { if (live) setSubClients(listOf(r)); }).catch(() => {});
        }
        return () => { live = false; };
    }, [ccCode, client]);

    useEffect(() => {
        let live = true;
        setPos([]);
        if (client && subClient) {
            // Passing the entry's Id keeps its own PO in the list.
            getRoute('Accounts/GetClientPOforWP', { CCCode: ccCode, Client: client, SubClient: subClient, Id: wpId })
                .then((r) => { if (live) setPos(listOf(r)); }).catch(() => {});
        }
        return () => { live = false; };
    }, [ccCode, client, subClient, wpId]);

    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
                <label className={lblCls}>Client <span className="text-red-500">*</span></label>
                <select className={selCls} value={client || ''}
                    onChange={(e) => setValues((p) => ({ ...p, Clientcode: e.target.value, SubClientcode: '', PONumber: '' }))}>
                    <option value="">-Please Select-</option>
                    {clients.map((c) => <option key={c.ClientCode} value={c.ClientCode}>{c.ClientName}</option>)}
                </select>
            </div>
            <div>
                <label className={lblCls}>Sub Client <span className="text-red-500">*</span></label>
                <select className={selCls} value={subClient || ''}
                    onChange={(e) => setValues((p) => ({ ...p, SubClientcode: e.target.value, PONumber: '' }))}>
                    <option value="">-Please Select-</option>
                    {subClients.map((s) => <option key={s.SubClientCode} value={s.SubClientCode}>{s.SubClientCodename}</option>)}
                </select>
            </div>
            <div>
                <label className={lblCls}>PO Number <span className="text-red-500">*</span></label>
                <select className={selCls} value={values.PONumber || ''}
                    onChange={(e) => setValues((p) => ({ ...p, PONumber: e.target.value }))}>
                    <option value="">-Please Select-</option>
                    {pos.map((po) => <option key={po.ClientPOId ?? po.PONumber} value={po.PONumber}>{po.PONumber}</option>)}
                </select>
            </div>
        </div>
    );
};

// ── Items Transfer (legacy VerifyItemsTransferView / ViewItemTransferDetailsGridView) ──────────────
// Levels: below the define level (central store keeper) the verifier only ticks items; at the define
// level they pick a depreciation % per asset; above it the CSK % is pre-selected and can be changed.
// Item codes starting with "1" are consumables (no depreciation).
const isConsumable = (it) => String(it.ItemCode || '').trim().startsWith('1');

// Legacy controller ViewItemTransferDetailsGridView: options from the configured max depreciation %.
// Issuing from the central store → 10…max (100 shown as "FullValue"); otherwise max…100.
export const itemsTransferDepOptions = (maxPercent, fromCC, centralStoreCC) => {
    const no = Math.floor((Number(maxPercent) || 0) / 10);
    if (no <= 0) return [];
    const opt = (v) => ({ value: v, label: v >= 100 ? 'FullValue' : String(v) });
    if (fromCC === centralStoreCC) {
        if (no === 10) return [...Array.from({ length: 9 }, (_, i) => opt((i + 1) * 10)), opt(100)];
        return Array.from({ length: no }, (_, i) => opt((i + 1) * 10));
    }
    if (no === 10) return [opt(100)];
    return Array.from({ length: 10 - no + 1 }, (_, i) => opt((no + i) * 10));
};

// Selected % for a row: the verifier's pick, else (above the define level) the stored CSK %.
export const itemsTransferDep = (it, ext, above) => {
    const picked = (ext.dep || {})[it.ItId];
    if (picked !== undefined) return picked;
    if (!above || it.CskPercent == null || it.CskPercent === '') return '';
    return it.CskPercent === 'Full Value' ? '100' : String(parseInt(it.CskPercent, 10));
};

// After-depreciation value shown in the last column (legacy Calculate()).
const itemsTransferAfterDep = (it, ext, above) => {
    const base = above ? it.CskDep : it.AfterDep;
    if (isConsumable(it) || (ext.dep || {})[it.ItId] === undefined) return base;
    const dep = (ext.dep || {})[it.ItId];
    if (dep === '') return '';
    if (Number(dep) === 100) return 0;
    return ((Number(it.Amount) || 0) * (100 - Number(dep))) / 100;
};

export const ItemsTransferItemsTable = ({ items, levels, depOptions, ext, setExt }) => {
    const present = Number(levels.IndentPresentLevel);
    const define = Number(levels.IndentDefineLevel);
    const atOrAbove = present >= define;
    const above = present > define;
    const setDep = (it, v) => setExt((p) => ({ ...p, dep: { ...(p.dep || {}), [it.ItId]: v } }));
    const depSelect = (it) => (
        <select
            value={itemsTransferDep(it, ext, above)}
            disabled={isConsumable(it)}
            onChange={(e) => setDep(it, e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
        >
            <option value="">Select</option>
            {depOptions.map((o) => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
        </select>
    );
    const sumTotal = items.reduce((a, it) => a + (Number(it.Amount) || 0), 0);
    const depTotal = items.reduce((a, it) => a + (isConsumable(it) ? 0 : Number(itemsTransferAfterDep(it, ext, above)) || 0), 0);
    const columns = [
        { label: 'Item Code', render: (it) => it.ItemCode },
        { label: 'Item Name', render: (it) => it.ItemName, align: 'left' },
        { label: 'Specification', render: (it) => it.Specification, align: 'left' },
        { label: 'DCA', render: (it) => it.DcaCode },
        { label: 'Sub DCA', render: (it) => it.SubDcaCode },
        ...(atOrAbove ? [{ label: 'Basic Price', render: (it) => fmt(it.Basic), align: 'right' }] : []),
        { label: 'Units', render: (it) => it.Units },
        { label: 'Issued Qty', render: (it) => it.IssQuantity, align: 'right' },
        { label: 'Item Status', render: (it) => it.ItemStatus },
        ...(atOrAbove ? [
            { label: above ? 'Amount' : 'Before Depreciation', render: (it) => fmt(it.Amount), align: 'right' },
            { label: above ? 'CSK %' : 'Depreciation', render: depSelect },
            { label: above ? 'CSK Depreciation' : 'Depreciation Value', render: (it) => fmt(itemsTransferAfterDep(it, ext, above)), align: 'right' },
        ] : []),
    ];
    return (
        <CheckedItemsTable
            title="Transfer Items"
            items={items}
            columns={columns}
            ext={ext}
            setExt={setExt}
            footer={atOrAbove ? (
                <>
                    <td colSpan={columns.length - 2} className="px-3 py-2 text-xs font-bold text-right text-gray-700 dark:text-gray-300">Sum Total: {fmt(sumTotal) || '0'}</td>
                    <td className="px-3 py-2 text-xs font-bold text-right text-gray-700 dark:text-gray-300">After Depreciation</td>
                    <td className="px-3 py-2 text-xs font-bold text-right text-emerald-600">{fmt(depTotal) || '0'}</td>
                </>
            ) : null}
        />
    );
};

// Legacy ApproveItemstransferDetails payload for the define level and above (CSV lists end with a comma).
// Kept as legacy computes it, including resetting the before/after sums when a row is at full value.
export const itemsTransferApprovalAtDefine = (items, ext, above, action) => {
    let rowIds = '';
    let deps = '';
    let depAmounts = '';
    let amount = 0;
    let effAmount = 0;
    let sumBefore = 0;
    let sumAfter = 0;
    const reject = String(action).toLowerCase() === 'reject';
    items.forEach((it) => {
        rowIds += `${it.ItId},`;
        if (isConsumable(it)) return;
        const value = Number(it.Amount) || 0;
        const dep = itemsTransferDep(it, ext, above);
        if (Number(dep) !== 100) {
            const pct = Number(dep) || 0;
            deps += `${dep},`;
            depAmounts += `${value - (value * pct) / 100},`;
            if (reject) {
                amount = 0; effAmount = 0; sumBefore = 0; sumAfter = 0;
            } else {
                amount += value - (value * pct) / 100;
                effAmount += (value * pct) / 100;
                sumBefore += value;
                sumAfter += Number(itemsTransferAfterDep(it, ext, above)) || 0;
            }
        } else {
            depAmounts += '0,';
            deps += 'Full Value,';
            if (reject) amount = 0; else amount += value;
            sumBefore = 0;
            sumAfter = 0;
        }
    });
    return { rowIds, deps, depAmounts, amount, effAmount, sumBefore, sumAfter };
};
export const itemsTransferIsConsumable = isConsumable;

// Generic popup: GET route(params) → table. heads: column titles; row(item) → cells; alignRight: column indexes.
// Portaled above the detail popup like TradeItemSummaryModal.
export const RouteTableModal = ({ title, route, params, heads, row, alignRight = [], onClose }) => {
    const [rows, setRows] = useState(null);
    const queryKey = JSON.stringify(params || {});
    useEffect(() => {
        let live = true;
        getRoute(route, JSON.parse(queryKey))
            .then((r) => { if (live) setRows(listOf(r)); })
            .catch(() => { if (live) setRows([]); });
        return () => { live = false; };
    }, [route, queryKey]);

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <p className="font-bold">{title}</p>
                    <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 max-h-[60vh] overflow-auto">
                    {rows === null
                        ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
                        : <SimpleTable heads={heads} rows={rows.map(row)} alignRight={alignRight} />}
                </div>
            </div>
        </div>,
        document.body,
    );
};

// ── Vendor CMS Payment (legacy ViewCMSPaymentAddedVerificationData / …InnerData) ────────────────────
// Vendors in the CMS batch, each expandable to its invoices. The verifier ticks the vendors to pay
// (ext.cmsVendors) and may drop a ticked invoice from the batch (RemoveCMSSingleVerInvoices).
const resultStatus = (r) => (typeof r === 'string' ? r : r?.Data ?? r?.saveStatus ?? '');

export const VendorCMSVerifyGrid = ({ tranNo, ext, setExt, toast }) => {
    const [vendors, setVendors] = useState(null);
    const [open, setOpen] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [invTicks, setInvTicks] = useState({});
    const [busy, setBusy] = useState(false);
    const ticks = ext.cmsVendors || {};

    const loadVendors = useCallback(() => {
        getRoute('Purchase/ViewCMSPaymentAddedVerificationData', { Trno: tranNo })
            .then((r) => setVendors(listOf(r))).catch(() => setVendors([]));
    }, [tranNo]);
    const loadInvoices = useCallback((code) => {
        setInvoices([]);
        setInvTicks({});
        if (!code) return;
        getRoute('Purchase/ViewCMSPaymentAddedVerificationInnerData', { Vendorcode: code, TranNo: tranNo })
            .then((r) => setInvoices(listOf(r))).catch(() => setInvoices([]));
    }, [tranNo]);

    useEffect(() => { setVendors(null); setOpen(null); loadVendors(); }, [loadVendors]);
    useEffect(() => { loadInvoices(open); }, [open, loadInvoices]);

    const remove = async (inv) => {
        if (!invTicks[inv.Id]) { toast.error('Please Check for Remove'); return; }
        setBusy(true);
        try {
            const r = await postRoute('Purchase/RemoveCMSSingleVerInvoices', {
                RID: String(inv.Id), VendorCode: inv.VendorCode, Veninvid: String(inv.Veninvid), TransactionNo: inv.TransactionNo || tranNo,
            });
            const st = resultStatus(r);
            if (/^Submitt?ed$/.test(st)) {
                toast.success('Removed Successfully');
                loadVendors();
                loadInvoices(open);
            } else {
                toast.error(st || 'Error Occurred While Deleting Item');
            }
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Error Occurred While Deleting Item');
        } finally {
            setBusy(false);
        }
    };

    if (vendors === null) {
        return <Block title="Vendors"><div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div></Block>;
    }
    const total = vendors.reduce((a, v) => a + (Number(v.BasicBalance) || 0), 0);
    const cell = 'px-3 py-2 text-xs text-gray-700 dark:text-gray-300';
    const toggleVendor = (code) => setExt((p) => ({ ...p, cmsVendors: { ...(p.cmsVendors || {}), [code]: !(p.cmsVendors || {})[code] } }));
    return (
        <Block title="Vendors">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Tick the vendors to pay; expand a vendor to review or remove its invoices.</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="w-full">
                    <thead>
                        <tr className="bg-gradient-to-r from-indigo-600 to-purple-600 text-[10px] font-bold text-white uppercase">
                            <th className="px-3 py-2 w-8"></th>
                            <th className="px-3 py-2">Chk</th>
                            <th className="px-3 py-2 text-left">Vendor Code</th>
                            <th className="px-3 py-2 text-left">Vendor Name</th>
                            <th className="px-3 py-2 text-right">Paid Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vendors.map((v) => (
                            <React.Fragment key={v.VendorCode}>
                                <tr className="border-t border-gray-100 dark:border-gray-700">
                                    <td className={cell}>
                                        <button type="button" onClick={() => setOpen(open === v.VendorCode ? null : v.VendorCode)} className="text-indigo-600 dark:text-indigo-400">
                                            {open === v.VendorCode ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                        </button>
                                    </td>
                                    <td className={`${cell} text-center`}>
                                        <button type="button" className="text-indigo-600 dark:text-indigo-400" onClick={() => toggleVendor(v.VendorCode)}>
                                            {ticks[v.VendorCode] ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                        </button>
                                    </td>
                                    <td className={cell}>{v.VendorCode}</td>
                                    <td className={cell}>{v.VendorName}</td>
                                    <td className={`${cell} text-right`}>{fmt(v.BasicBalance)}</td>
                                </tr>
                                {open === v.VendorCode && (
                                    <tr>
                                        <td colSpan={5} className="bg-gray-50 dark:bg-gray-800/60 px-4 py-3">
                                            <SimpleTable
                                                heads={['Chk', 'Cost Center', 'Invoice Date', 'Invoice No', 'PO No', 'Invoice Balance', '']}
                                                rows={invoices.map((inv) => [
                                                    <input key="c" type="checkbox" checked={!!invTicks[inv.Id]} onChange={() => setInvTicks((p) => ({ ...p, [inv.Id]: !p[inv.Id] }))} />,
                                                    inv.CCCode, inv.InvoiceDate, inv.Invoiceno, inv.PoNo, fmt(inv.BasicBalance),
                                                    <button key="r" type="button" disabled={busy} onClick={() => remove(inv)}
                                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">
                                                        <Trash2 className="w-3 h-3" /> Remove
                                                    </button>,
                                                ])}
                                                alignRight={[5]}
                                                foot={invoices.length ? ['', '', '', '', 'Total', fmt(invoices.reduce((a, x) => a + (Number(x.BasicBalance) || 0), 0)), ''] : undefined}
                                            />
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                        {!vendors.length && <tr><td colSpan={5} className={`${cell} text-center py-4`}>No vendors in this payment</td></tr>}
                    </tbody>
                    {vendors.length > 0 && (
                        <tfoot>
                            <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
                                <td colSpan={4} className={`${cell} font-bold text-right`}>Total</td>
                                <td className={`${cell} font-bold text-right text-emerald-600`}>{fmt(total)}</td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </Block>
    );
};

// Legacy ExportExcelVendorcmspayment: the bank's CMS upload sheet, column titles as the MVC controller wrote them.
export const VENDOR_CMS_EXCEL_COLUMNS = [
    ['Transaction Type', 'TransactionType'], ['Beneficiary Code', 'BeneficiaryCode'], ['Beneficiary Account Number', 'BeneficiaryAccountNumber'],
    ['Instrument Amount', 'InstrumentAmount'], ['Beneficiary Name', 'BeneficiaryName'], ['Drawee Location', 'DraweeLocation'],
    ['Print Location', 'PrintLocation'], ['Bene Address 1', 'BeneAddress1'], ['Bene Address 2', 'BeneAddress2'], ['Bene Address 3', 'BeneAddress3'],
    ['Bene Address 4', 'BeneAddress4'], ['Bene Address 5', 'BeneAddress5'], ['Instruction Reference Number', 'InstructionReferenceNumber'],
    ['Customer Reference Number', 'CustomerReferenceNumber'], ['Payment details 1', 'Paymentdetails1'], ['Payment details 2', 'Paymentdetails2'],
    ['Payment details 3', 'Paymentdetails3'], ['Payment details 4', 'Paymentdetails4'], ['Payment details 5', 'Paymentdetails5'],
    ['Payment details 6', 'Paymentdetails6'], ['Payment details 7', 'Paymentdetails7'], ['ChequeNumber', 'ChequeNumber'],
    ['Chq/Trn Date', 'ChqTrnDate'], ['MICR Number', 'MICRNumber'], ['IFSC Code', 'IFSCCode'], ['Bene Bank Name', 'BeneBankName'],
    ['Bene Bank Branch Name', 'BeneBankBranchName'], ['Beneficiary email id', 'Beneficiaryemailid'],
];
export const downloadVendorCMSExcel = async (tranNo) => {
    const rows = listOf(await getRoute('Purchase/ExportExcelVendorcmspayment', { TranNo: tranNo }));
    const sheet = XLSX.utils.aoa_to_sheet([
        VENDOR_CMS_EXCEL_COLUMNS.map(([title]) => title),
        ...rows.map((r) => VENDOR_CMS_EXCEL_COLUMNS.map(([title, key]) => (title === 'Instrument Amount' ? Number(r[key]) || 0 : r[key] ?? ''))),
    ]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'VendorCMSPaymentExcel');
    XLSX.writeFile(book, `VendorCMSSheet_${tranNo}.xlsx`);
};
